import queryKeys from '#hooks/queries/queryKeys';
import usePostCoachMemoMutation from '#hooks/queries/schedules/usePostCoachMemoMutation';
import CoachMark from '#shared/CoachMark';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import Icon from 'src/components/atoms/Icon';
import useScheduleRegistrationUrl from '../../hooks/useScheduleRegistrationUrl';
import { CalendarPeriodic } from '../../types';
import ModalEditMemo from '../ModalEditMemo';
import * as S from './styles';

interface Props {
  activeDate?: dayjs.Dayjs;
  defaultCalendarPeriodic?: CalendarPeriodic;
  onChangePeriodic: (periodic: CalendarPeriodic) => void;
}

export default function CalendarHeader({
  activeDate,
  defaultCalendarPeriodic = 'WEEKLY',
  onChangePeriodic
}: Props) {
  const queryClient = useQueryClient();
  const [isOpenModalMemo, setIsOpenModalMemo] = useState<boolean>();
  const [calendarPeriodic, setCalendarPeriodic] = useState<CalendarPeriodic>(
    defaultCalendarPeriodic
  );
  const navigate = useNavigate();
  const registrationScheduleUrl = useScheduleRegistrationUrl();
  const { mutateAsync: postMemo } = usePostCoachMemoMutation();

  const isWeekly = calendarPeriodic === 'WEEKLY';

  return (
    <S.Wrapper style={{ marginBottom: '0px' }}>
      <S.LeftPanel>
        <CoachMark.SpotLight style={{ padding: '8px', borderRadius: '80px' }}>
          <CoachMark.Tooltip
            position="top"
            message={`일간/주간 스케쥴을\n전환할 수 있어요`}
            tooltipStyle={{
              left: undefined,
              right: '19px',
              transform: undefined
            }}
            tooltipArrowStyle={{
              left: undefined,
              right: '10px',
              transform: undefined
            }}
          />
          <S.ButtonSelectPeriodic
            onClick={() => {
              const newState =
                calendarPeriodic === 'DAILY' ? 'WEEKLY' : 'DAILY';
              onChangePeriodic(newState);
              setCalendarPeriodic(newState);
            }}
          >
            {isWeekly ? <S.WeekPerfiodicIcon /> : <S.DayPerfiodicIcon />}
            {isWeekly ? '주간' : '일간'}
          </S.ButtonSelectPeriodic>
        </CoachMark.SpotLight>
      </S.LeftPanel>
      <CoachMark.SpotLight style={{ padding: '8px', borderRadius: '12px' }}>
        <CoachMark.Tooltip
          position="top"
          message={`메모장과 공지사항을 \n 등록하고 ‘+’ 버튼을 \n 통해 일정을 추가해요`}
          tooltipStyle={{
            left: undefined,
            right: '0',
            transform: undefined
          }}
          tooltipArrowStyle={{
            left: undefined,
            right: '10px',
            transform: undefined
          }}
        />
        <S.ButtonGroup>
          <S.ButtonMemo onClick={() => setIsOpenModalMemo(true)}>
            <Icon name="IconMemo" />
          </S.ButtonMemo>
          <S.ButtonAddSchedule
            onClick={() => {
              navigate(registrationScheduleUrl as string);
            }}
          >
            <Icon name="IconPlus" width={22} height={22} color="#fff" />
          </S.ButtonAddSchedule>
        </S.ButtonGroup>
      </CoachMark.SpotLight>

      {isOpenModalMemo && (
        <ModalEditMemo
          calendarPeriodic={calendarPeriodic}
          activeDate={activeDate as dayjs.Dayjs}
          open={isOpenModalMemo}
          handleClose={() => setIsOpenModalMemo(false)}
          onSubmit={async (memo, activeTab) => {
            const payload: Record<string, number | string | undefined> = {
              memoType: activeTab,
              memo,
              date: activeDate?.format('YYYY-MM-DD')
            };

            if (activeTab === 'WEEKLY') {
              payload['year'] = activeDate?.get('year');
              payload['week'] = activeDate?.week();
            }
            // @ts-ignore
            await postMemo(payload);

            queryClient.invalidateQueries([queryKeys.SCHEDULE, 'memo']);

            setIsOpenModalMemo(false);
          }}
        />
      )}
    </S.Wrapper>
  );
}
